import { i18nMark } from '@lingui/react';

import { LOCALES, getI18n } from '@/utils/i18n';

const i18n = getI18n();

export const CURRENT_COUNTRY_CALLING_CODE = '86'; // type string

export const ENTER_KEY_CODE = 13;

export const DEFAULT_NETWORK_ERROR_MESSAGE = i18n.t`网络错误，请重试`;

// Number of items being fetched each time.
export const DEFAULT_PAGE_LIMIT = 30;

// Candidate attachment size:
export const MAX_CANDIDATE_ATTACHMENT_SIZE = 50 * 1024 * 1024;

/**
 * Date time format string.
 */
export const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const DATE_FORMAT = 'YYYY-MM-DD';

export const RESUME_ACCEPTABLE_NOTICE = i18n.t`支持pdf、doc、xls、ppt、docx、pptx、wps、jpg、jpeg、png、txt等简历格式`;

export const ATTACHMENT_ACCEPTABLE_TYPES = [
  // 视频
  'video/x-ms-asf', // .wmv
  'video/mp4', // .mp4, .m4v
  'video/3gpp', // .3gp
  'video/quicktime', // .mov
  'video/x-msvideo', // .avi
  'video/x-matroska',
  'video/x-mkv',
  '.mkv', // .mkv
  'video/x-flv', // .flv
  // 音频
  'audio/vnd.wave',
  'audio/wav',
  'application/wav',
  'audio/wave',
  'audio/x-wav', // .wav
  'audio/x-flac',
  'audio/flac', // .flac
  'audio/ape',
  'audio/x-ape',
  '.ape', // .ape
  'audio/mpeg',
  'audio/mpeg3',
  'audio/x-mpeg', // .mp3
  '.mpeg', // .mpeg
  'application/ogg',
  'audio/ogg', // .ogg
  'audio/vorbis',
  'audio/x-vorbis',
  '.vorbis', // .vorbis
  'audio/midi',
  'audio/x-midi', // .midi
  // 图片与设计
  'image/bmp',
  'image/x-ms-bmp', // .bmp
  'image/jpeg', // .jpg
  'image/pjpeg', // .jpeg
  'image/png', // .png
  'image/tiff', // .tif, .tiff
  'image/gif', // .gif
  'application/tga',
  'application/x-targa',
  'application/x-tga',
  'image/targa',
  'image/x-targa',
  'image/x-tga', // .tga
  'image/photoshop',
  'image/x-photoshop',
  'image/psd',
  'application/photoshop', // .psd
  'application/cdr',
  'application/coreldraw',
  'application/x-cdr',
  'application/x-coreldraw',
  '.cdr', // .cdr
  'image/pcd',
  'image/x-photo-cd',
  '.pcd', // .pcd
  'application/illustrator',
  '.ai', // .ai
  'image/x-dcraw',
  '.raw', // .raw
  'image/webp', // .webp
  'image/svg+xml', // .svg
  '.sketch', // .sketch
  // 压缩包
  'application/zip',
  'application/x-zip-compressed', // .zip
  'application/rar',
  'application/x-rar-compressed', // .rar
  'application/x-7z-compressed', // .7z
  // 文档
  'application/pdf', // .pdf
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/vnd.ms-excel',
  'application/xls',
  'application/x-xls',
  '.xls', // .xls
  'application/msword',
  '.doc', // .doc
  'application/vnd.ms-powerpoint',
  '.ppt', // .ppt
  'text/plain', // .txt
  'text/html', // .html, .htm
  'application/vnd.ms-works',
  'application/x-msworks-wp',
  'zz-application/zz-winassoc-wps',
  '.wps', // .wps
  '.mind', // .mind
  'application/vnd.xmind.workbook', // .xmind
  'text/xml', // .xml
  'text/markdown',
  'text/x-markdown', // .md
].join();

// 角色类型
export const ROLES = {
  NEWBIE: 0,
  FOREGROUND: 5, // 前台小妹
  // 面试官
  INTERVIEWER: 10,
  // 用人经理
  HIRING_MANAGER: 20,
  // 高级用人经理
  HIGH_HIRING_MANAGER: 25,
  // hr
  HR: 30,
  // 管理员
  ADMIN: 40,
  // 超级管理员
  SUPERADMIN: 50,
};

export const ROLE_NAMES = {
  [ROLES.NEWBIE]: i18nMark('内推人'),
  [ROLES.FOREGROUND]: i18nMark('前台'),
  [ROLES.INTERVIEWER]: i18nMark('面试官'),
  [ROLES.HIRING_MANAGER]: i18nMark('用人经理'),
  [ROLES.HIGH_HIRING_MANAGER]: i18nMark('高级用人经理'),
  [ROLES.HR]: i18nMark('HR'),
  [ROLES.ADMIN]: i18nMark('管理员'),
  [ROLES.SUPERADMIN]: i18nMark('超级管理员'),
};

export const ACCEPTED_RESUME_MIMETYPE = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/wps-office.doc',
  'application/wps-office.docx',
  '.doc',
  '.docx',
  'application/mspowerpoint',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/wps-office.ppt',
  'application/wps-office.pptx',
  '.ppt',
  '.pptx',
  // 'text/html', // chrome有bug，如果开启html格式，打开upload dialog会变得特别卡
  '.html',
  'image/jpeg',
  '.jpg',
  '.jpeg',
  'image/png',
  '.png',
  'application/rtf',
  'application/x-rtf',
  'text/richtext',
  'text/rtf',
  '.rtf',
  '.wps',
  'text/plain',
  '.txt',
  // 'multipart/related', // chrome有bug，如果开启mht格式，打开upload dialog会变得特别卡
  // 'message/rfc822', // chrome有bug，如果开启eml格式，打开upload dialog会变得特别卡
].join();

// 备注@人 LRU cache
export const LS_RECENT_MENTIONED_USER_IDS = 'recentMentionedIds';
// 页面重载后是否需要展示隐私政策
export const LS_SHOW_SECRET_POLICY = 'showSecretPolicy';

// 如果存在，小于当前时间不提醒，大于当前时间，提醒更新密码并且设置为当前时间五天后
export const LS_PASSWORD_UPDATE_NOTIFICATION = 'LS_PASSWORD_UPDATE_NOTIFICATION';
export const LS_HEADHUNTER_PASSWORD_UPDATE_NOTIFICATION =
  'LS_HEADHUNTER_PASSWORD_UPDATE_NOTIFICATION'; // 猎头端的。

// hr端猎头设置中的候选人状态
export const HEADHUNTER_APPLICATION_STATUS = {
  BLUR_STATUS: 0,
  REAL_STATUS: 1,
};

// 申请附件类型
export const APPLICATION_ATTACHMENT_TYPE = {
  CANDIDATE_UPLOAD_ATTACHMENT: 'CANDIDATE_UPLOAD_ATTACHMENT', // 候选人上传的附件自定义附件和系统自带的附件（场景：填写登记表形成的附件）
  UPLOAD_PORTRAIT: 'UPLOAD_PORTRAIT', // 头像附件（场景：标准简历上传头像， 登记表上传头像）
  HR_UPLOAD_ATTACHMENT: 'HR_UPLOAD_ATTACHMENT', // hr上传附件（场景：hr端附件信息手动上传附件）
  HM_UPLOAD_ATTACHMENT: 'HM_UPLOAD_ATTACHMENT', // 用人经历端上传附件（场景：用人经历端附件信息手动上传附件）
  INTERVIEW_ACCEPT: 'INTERVIEW_ACCEPT', // 候选人接受面试生成的登记表附件
  INTERVIEW_SIGNIN: 'INTERVIEW_SIGNIN', // 候选人面试签到生成的登记表附件
  OFFER_ACCEPT: 'OFFER_ACCEPT', // 候选人接受OFFER生成的登记表附件
  MERGE_APPLICATION_ATTACHMENT: 'MERGE_APPLICATION_ATTACHMENT', // 候选人合并产生的附件
  INVITE_UPDATE_RESUME: 'INVITE_UPDATE_RESUME', // 邀请候选人更新简历生成的登记表
  ID_CARD_BACK: 'ID_CARD_BACK', // 身份证正面
  ID_CARD_FRONT: 'ID_CARD_FRONT', // 身份证背面
};

/**
 * 招聘模式(社招，校招)
 * 因为存储时会将所有模式进行或运算存储
 * 所以必须选择2进制时仅有一位是1的数字
 * 也就是说新增的modeId必须是当前最大Id*2
 * 1，2，4，8，16这样的
 */
export const HIRE_MODES = {
  SOCIAL: 1,
  CAMPUS: 2,
};

export const HEADHUNTER_CONFIRM_STATUS = {
  HR_UN_HANDLE: 0,
  HR_ACCEPT: 1,
  HR_REJECT: 2,
  CANDIDATE_UN_CONFIRM: 3,
  CANDIDATE_REJECT: 4,
  SYSTEM_REJECT: 5, // 系统超时拒绝
};

/**
 * 登录页面所有类型的panel
 */
export const LOGIN_PANEL_TYPES = {
  login: 'login',
  forceLogin: 'forceLogin', // 强制登录。
  accountForbidden: 'accountForbidden', // 账号被禁用。
  chooseOrgLogin: 'chooseOrgLogin', // 多 org 账号选择 org 登录
  smsLogin: 'smsLogin', // 短信验证码登录
  dtLogin: 'dtLogin', // 钉钉登录
};

/**
 * 登录账号类型，只写了USER和猎头
 */
export const ACCOUNT_TYPES = {
  USER: 'USER',
  HEADHUNTER: 'HEADHUNTER',
};

export const PWD_MIN_LENGTH = 8; // 密码最小长度。

export const PWD_LEVELS = {
  OLD: 0, // 新的代码规则之前生成的代码。
  ROBUST: 1,
};

export const PROTECT_TYPES = {
  RECOMMENDER_PROTECT: 'recommenderProtect',
  HEADHUNTER_PROTECT: 'headhunterProtect',
  OWN_PROTECT: 'ownProtect',
  HEADHUNTER_LOCK_IN_PROCESS: 'headhunterLockInProcess',
  CAMPUS_AMBASSADOR_PROTECT: 'campusAmbassadorProtect',
};

/**
 * 猎头接收拒绝状态
 */
export const CONFIRMATION_STATUS = {
  [HEADHUNTER_CONFIRM_STATUS.HR_UN_HANDLE]: i18n.t`未处理`,
  [HEADHUNTER_CONFIRM_STATUS.HR_ACCEPT]: i18n.t`接收`,
  [HEADHUNTER_CONFIRM_STATUS.HR_REJECT]: i18n.t`拒绝`,
  [HEADHUNTER_CONFIRM_STATUS.CANDIDATE_UN_CONFIRM]: i18n.t`候选人待确认`,
  [HEADHUNTER_CONFIRM_STATUS.CANDIDATE_REJECT]: i18n.t`候选人拒绝`,
  [HEADHUNTER_CONFIRM_STATUS.SYSTEM_REJECT]: i18n.t`未在时效内确认，该推荐系统已默认拒绝`,
};

/**
 * 猎头申请列表其他阶段
 */
export const ADDITIONAL_STAGES = {
  PROCESS: i18nMark('进行中'),
  REJECTED: i18nMark('淘汰'),
  HIRED: i18nMark('已入职'),
  DELETED: i18nMark('被HR从系统中删除'),
};

/**
 * 猎头申请列表其他阶段选项
 */
export const ADDITIONAL_STAGE_OPTIONS = {
  PROCESS: { label: i18n.t`进行中`, value: i18nMark('进行中') },
  REJECTED: { label: i18n.t`淘汰`, value: i18nMark('淘汰') },
  HIRED: { label: i18n.t`已入职`, value: i18nMark('已入职') },
  DELETED: {
    label: i18n.t`被HR从系统中删除`,
    value: i18nMark('被HR从系统中删除'),
  },
};

// 隐私协议相关名称
// eslint-disable-next-line @moka-fe/lingui-mark
export const PRIVACY_TYPE = { AGREEMENT: '《Moka隐私政策》' };

/**
 * APM 要记录的页面信息
 */
export const RECORD_APM_PAGE_INFO = {
  JOBS: { name: 'JOBS' },
  APPLICATIONS: { name: 'APPLICATIONS' },
  JOB_DETAIL: { name: 'JOB_DETAIL' },
  RECOMMEND: { name: 'RECOMMEND' },
  RECOMMEND_RESULT: { name: 'RECOMMEND_RESULT' },
  RECOMMEND_EXCEL_RESULT: { name: 'RECOMMEND_EXCEL_RESULT' },
};

/**
 * APM 记录页面进入的方式
 */
export const RECORD_APM_ENTER_PAGE_TYPE = {
  FIRST: 1, // 首次加载进入
  CHANGE: 2, // 路由切换
};

export const JOB_STATUS = {
  TEMP: 'temp',
  OPEN: 'open',
  CLOSED: 'closed',
  PAUSE: 'pause',
};
/**
 * 职位列表 分类TAB
 * 与 职位状态不是一一对应
 */
export const JOB_LIST_TYPES = {
  OPEN: 'open',
  CLOSED: 'closed',
  ALL: 'all',
};

export const JOB_LIST_TYPE_TO_JOB_STATUS = {
  [JOB_LIST_TYPES.ALL]: [JOB_STATUS.OPEN, JOB_STATUS.CLOSED, JOB_STATUS.PAUSE],
  [JOB_LIST_TYPES.OPEN]: [JOB_STATUS.OPEN],
  [JOB_LIST_TYPES.CLOSED]: [JOB_STATUS.CLOSED, JOB_STATUS.PAUSE],
};

// 证件类型
export const CERTIFICATE_TYPE_OPTIONS = [
  { label: i18n.t`身份证`, value: 1, alias: 'CITIZEN_ID' },
  { label: i18n.t`身份证(中国香港)`, value: 2, alias: 'CITIZEN_ID_HK' },
  { label: i18n.t`身份证(中国澳门)`, value: 3, alias: 'CITIZEN_ID_MO' },
  { label: i18n.t`身份证(中国台湾)`, value: 4, alias: 'CITIZEN_ID_TW' },
  { label: i18n.t`港澳居民来往内地通行证`, value: 7, alias: 'MAINLAND_TRAVEL_PERMIT_HK' },
  { label: i18n.t`台湾居民来往内地通行证`, value: 8, alias: 'MAINLAND_TRAVEL_PERMIT_TW' },
  { label: i18n.t`外国人工作许可证`, value: 9, alias: 'FOREIGNER_WORK_PERMIT' },
  { label: i18n.t`外国人居留许可证`, value: 10, alias: 'FOREIGNER_RESIDENCE_PERMIT' },
  { label: i18n.t`护照`, value: 5, alias: 'PASSPORT' },
  { label: i18n.t`其他证件`, value: 6, alias: 'OTHER' },
];

export const CERTIFICATE_TYPE = CERTIFICATE_TYPE_OPTIONS.reduce((o, option) => {
  o[option.alias] = option.value;
  return o;
}, {});

export const HIRE_MODE_NAMES = {
  [HIRE_MODES.SOCIAL]: i18n.t`社招`,
  [HIRE_MODES.CAMPUS]: i18n.t`校招`,
};

export const LOCALES_LABEL = {
  [LOCALES.ZH_CN]: i18nMark('中文'),
  [LOCALES.EN_US]: i18nMark('English'),
};

export const isDingtalk = window._MOKA_ENV_?.IS_DINGTALK;

export const HEADHUNTER_TYPE = {
  HEADHUNTER: 1,
  RPO: 2,
};

export const SET_ACTION = {
  ADD: 'add',
  DELETE: 'delete',
};

export const JOB_HR_ASSISTANT = 25;
export const JOB_HR = 30;

/** 薪资单位 */
export const SALARY_UNIT = {
  K_MONTH: 0,
  YUAN_MONTH: 1,
  YUAN_WEEK: 2,
  YUAN_DAY: 3,
  YUAN_HOUR: 4,
  YUAN_EVERY_TIME: 5,
};

/** 薪资单位（海外 aws） */
export const SALARY_OVERSEA_UNIT = {
  YEAR: 11,
  MONTH: 6,
  WEEK: 7,
  HOUR: 9,
  DAY: 8,
  EVERY_TIME: 10,
};

export const SALARY_UNIT_NAMES = {
  [SALARY_UNIT.K_MONTH]: i18n.t`K/月`,
  [SALARY_UNIT.YUAN_MONTH]: i18n.t`元/月`,
  [SALARY_UNIT.YUAN_WEEK]: i18n.t`元/周`,
  [SALARY_UNIT.YUAN_DAY]: i18n.t`元/天`,
  [SALARY_UNIT.YUAN_HOUR]: i18n.t`元/小时`,
  [SALARY_UNIT.YUAN_EVERY_TIME]: i18n.t`元/次`,
  [SALARY_OVERSEA_UNIT.MONTH]: 'per month',
  [SALARY_OVERSEA_UNIT.WEEK]: 'per week',
  [SALARY_OVERSEA_UNIT.DAY]: 'per day',
  [SALARY_OVERSEA_UNIT.HOUR]: 'per hour',
  [SALARY_OVERSEA_UNIT.EVERY_TIME]: 'per occasion',
  [SALARY_OVERSEA_UNIT.YEAR]: 'per year',
};

/** 薪资结算方式 */
export const SALARY_PAYMENT_METHOD = {
  DAY: 0,
  WEEK: 1,
  MONTH: 2,
  END: 3,
};

export const SALARY_PAYMENT_METHOD_NAMES = {
  [SALARY_PAYMENT_METHOD.DAY]: i18n.t`日结`,
  [SALARY_PAYMENT_METHOD.WEEK]: i18n.t`周结`,
  [SALARY_PAYMENT_METHOD.MONTH]: i18n.t`月结`,
  [SALARY_PAYMENT_METHOD.END]: i18n.t`完工结`,
};

// i18nMark
export const JOB_VALID_COMMITMENT_OPTIONS = [
  i18nMark('全职'),
  i18nMark('兼职'),
  i18nMark('实习'),
  i18nMark('其它'),
];
